/**
 * FAZ 5 M6 — Global TCMB currency conversion helper.
 *
 * Reuse:
 *   - Backend endpoint: GET /api/currency/rates  (source of truth: forex_selling)
 *   - CurrencyChip / NumericInput mevcut merkezi component'ler
 *
 * Rate shape (frontend'te normalize edilir):
 *   { TRY: 1, USD: 48.2759, EUR: 51.34, GBP: 60.50 }
 *
 * Formül:
 *   from === to  →  amount değişmez
 *   tryValue     =  amount  ×  rates[fromCur]     (1 fromCur = X TRY)
 *   newAmount    =  tryValue / rates[toCur]       (1 toCur   = Y TRY)
 *
 * Guard:
 *   - Rate yoksa null döner (caller UX'i düzenler; NaN/Infinity üretmez)
 *   - amount null/undefined/"" ise null döner (input boşken çevirme)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export const SUPPORTED_CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

/**
 * @param {number|string} amount - kaynak tutar (string decimal desteklenir)
 * @param {string} fromCur - kaynak para birimi
 * @param {string} toCur - hedef para birimi
 * @param {Object} rates - {TRY:1, USD:X, EUR:Y, GBP:Z}  (rates[cur] = 1 CUR kaç TRY)
 * @returns {number|null} - yeni tutar veya null (rate yoksa)
 */
export function convertAmount(amount, fromCur, toCur, rates) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  const from = (fromCur || "TRY").toUpperCase();
  const to = (toCur || "TRY").toUpperCase();
  if (from === to) return n;
  if (!rates || typeof rates !== "object") return null;
  const fromRate = from === "TRY" ? 1 : Number(rates[from]);
  const toRate = to === "TRY" ? 1 : Number(rates[to]);
  if (!Number.isFinite(fromRate) || fromRate <= 0) return null;
  if (!Number.isFinite(toRate) || toRate <= 0) return null;
  const tryValue = n * fromRate;
  const result = tryValue / toRate;
  if (!Number.isFinite(result)) return null;
  // İç precision korunur; UI'da 2 decimal'a Number(x.toFixed(2)) ile yuvarla.
  return Math.round(result * 10000) / 10000;
}

/**
 * TCMB kur cache'ini merkezi hook üzerinden yükle.
 * Dönüş: { rates, ready, error, convert(amount, from, to), reload() }
 */
export function useCurrencyConversion(autoLoad = true) {
  const [rates, setRates] = useState(null);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  const reload = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      const { data } = await api.get("/currency/rates");
      // Response shape: { rates: [{currency, rate, rate_type, ...}] } veya list
      const arr = Array.isArray(data) ? data : (data?.rates || []);
      const map = { TRY: 1 };
      for (const r of arr) {
        // Yalnızca forex_selling (source of truth) — sessizce başka tipe geçme
        if ((r.rate_type || "forex_selling") !== "forex_selling") continue;
        const cur = String(r.currency || "").toUpperCase();
        const rate = Number(r.rate);
        if (SUPPORTED_CURRENCIES.includes(cur) && Number.isFinite(rate) && rate > 0) {
          map[cur] = rate;
        }
      }
      setRates(map);
      setError(null);
    } catch (e) {
      setError(e);
      setRates((prev) => prev || null);
    } finally {
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => { if (autoLoad) reload(); }, [autoLoad, reload]);

  const convert = useCallback((amount, from, to) => {
    return convertAmount(amount, from, to, rates);
  }, [rates]);

  return {
    rates,
    ready: !!rates,
    error,
    convert,
    reload,
  };
}

/**
 * Currency-change interaction helper.
 *
 * Kullanım (Products.jsx örnek):
 *   const { rates, convert } = useCurrencyConversion();
 *   const onPurchaseCurrencyChange = handleCurrencyChange({
 *     rates,
 *     convert,
 *     currentAmount: prodForm.purchase_price,
 *     currentCurrency: prodForm.purchase_currency,
 *     onApply: ({ amount, currency }) => setProdForm(p => ({ ...p, purchase_price: amount, purchase_currency: currency })),
 *   });
 *   ...
 *   <CurrencyChip value={...} onChange={onPurchaseCurrencyChange} />
 */
export function handleCurrencyChange({ rates, convert, currentAmount, currentCurrency, onApply, silent = false }) {
  return (newCurrency) => {
    const from = (currentCurrency || "TRY").toUpperCase();
    const to = (newCurrency || "TRY").toUpperCase();
    if (from === to) {
      onApply({ amount: currentAmount, currency: to });
      return;
    }
    // Amount boş/0 ise sadece currency değişsin
    const raw = currentAmount;
    if (raw === "" || raw === null || raw === undefined || Number(raw) === 0) {
      onApply({ amount: raw, currency: to });
      return;
    }
    if (!rates) {
      if (!silent) toast.error("Güncel döviz kuru alınamadığı için fiyat dönüştürülemedi.");
      // Currency değişikliğini iptal et (currentCurrency korunur)
      onApply({ amount: raw, currency: from });
      return;
    }
    const converted = convert(raw, from, to);
    if (converted === null) {
      if (!silent) toast.error(`Güncel ${to}/TRY kuru bulunamadı, dönüşüm iptal edildi.`);
      onApply({ amount: raw, currency: from });
      return;
    }
    // 2 decimal UI için yuvarla ama tam precision'ı tut
    const displayValue = Math.round(converted * 100) / 100;
    onApply({ amount: displayValue, currency: to });
    if (!silent) {
      const rate = rates[to] || 1;
      const fromRate = rates[from] || 1;
      // 1 to = X from (basit info)
      const humanRate = to === "TRY" ? fromRate : (to !== from ? (fromRate / rate).toFixed(4) : "1");
      toast.success(`TCMB kuru ile dönüştürüldü · 1 ${from} ≈ ${humanRate} ${to}`);
    }
  };
}
